import React, { useState } from "react";
import {
  Box,
  Container,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Grid,
  useTheme,
  useMediaQuery,
  Link,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { api } from "../api/api";
import { useSelector } from "react-redux";
import GradientButton from "../components/common/GradiantButton";
import SecondaryButton from "../components/common/SecondaryButton";

export default function RegisterPage() {
  const referId = useSelector((state) => state.referer.ReffererId);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    password: "",
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRegister = async () => {
    const { firstName, lastName, email, mobileNumber, password } = formData;
    setLoading(true);

    if (!firstName || !email || !mobileNumber || !password) {
      setLoading(false);
      setSnackbar({
        open: true,
        message: "Please fill in all required fields.",
        severity: "warning",
      });
      return;
    }

    const userData = {
      email,
      firstName,
      lastName,
      mobileNumber: parseInt(mobileNumber),
      password,
      pushToken: "",
      parentId: referId || null,
    };

    try {
      const response = await api.registerUser(userData);
      if (response.status === 200) {
        setSnackbar({
          open: true,
          message: "Check your email for verification link!",
          severity: "success",
        });
        setTimeout(() => {
          navigate("/login", { state: { from: location.state?.from } });
        }, 2000);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Registration failed!",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Grid
          container
          sx={{
            height: "auto",
            minHeight: "600px",
            maxHeight: "800px",
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e2e8f0",
          }}
        >
          {/* ✅ Left Side - Image Section */}
          <Grid
            item
            xs={12}
            md={5.4}
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              background: `
              linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5)),
              url("https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1973&q=80")
            `,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "linear-gradient(135deg, rgba(98,97,97,0.8) 0%, rgba(71,70,70,0.8) 100%)",
                zIndex: 1,
              }}
            />

            <Box
              sx={{
                textAlign: "center",
                color: "white",
                zIndex: 2,
                padding: 6,
                position: "relative",
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: "bold",
                  mb: 3,
                  textShadow: "2px 2px 8px rgba(0,0,0,0.5)",
                  fontSize: { md: "2.5rem", lg: "3rem" },
                }}
              >
                Join JEMINS Today 
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  opacity: 0.95,
                  textShadow: "1px 1px 4px rgba(0,0,0,0.5)",
                  maxWidth: "420px",
                  lineHeight: 1.6,
                  fontWeight: 300,
                  textAlign: "start",
                  mx: "auto",
                }}
              >
                Create your account and unlock access to exclusive properties,
                trusted listings, and personalized recommendations for your
                perfect home.
              </Typography>
            </Box>
          </Grid>

          {/* ✅ Right Side - Registration Form */}
          <Grid
            item
            xs={12}
            md={6.6}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#ffffff",
              order: { xs: 2, md: 2 },
            }}
          >
            <Box
              sx={{
                padding: { xs: 2, sm: 2, md: 2 },
                width: "100%",
                maxWidth: "500px",
              }}
            >
              <Box sx={{ textAlign: "center", mb: 5 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "bold",
                    color: "#1e293b",
                    mb: 2,
                    fontSize: { xs: "2rem", sm: "2.5rem" },
                  }}
                >
                  Create Account
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#64748b",
                    fontSize: "1.1rem",
                    fontWeight: 400,
                  }}
                >
                  Join our community today
                </Typography>
              </Box>

              {/* Form */}
              <Box
                component="form"
                noValidate
                autoComplete="off"
                sx={{ mt: 4 }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleRegister();
                  }
                }}
              >
                {["firstName", "lastName", "email", "mobileNumber"].map(
                  (field) => (
                    <TextField
                      key={field}
                      fullWidth
                      required={field !== "lastName"}
                      label={
                        field === "firstName"
                          ? "First Name"
                          : field === "lastName"
                            ? "Last Name"
                            : field === "email"
                              ? "Email"
                              : "Mobile Number"
                      }
                      name={field}
                      type={field === "email" ? "email" : field === "mobileNumber" ? "number" : "text"}
                      value={formData[field]}
                      onChange={handleChange}
                      margin="normal"
                      sx={{
                        mb: 0,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                            borderWidth: 2,
                          },
                        },
                      }}
                    />
                  )
                )}

                {/* Password Field with Eye Icon */}
                <TextField
                  fullWidth
                  required
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  margin="normal"
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                        borderWidth: 2,
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    mt: 1,
                    mb: 1,
                  }}
                >
                  <GradientButton
                    sx={{ width: { xs: "100%" } }}
                    fullWidth
                    text={
                      loading ? "Creating Account..." : "Create Account"
                    }
                    onClick={handleRegister}
                    disabled={loading}
                  />
                  <SecondaryButton
                    style={{ width: "100%" }}
                    fullWidth
                    text="Cancel"
                    onClick={() => navigate("/login")}
                  />
                </Box>

                {/* Login Link */}
                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    mt: 4,
                    color: "#64748b",
                    fontSize: "1rem",
                  }}
                >
                  Already have an account?{" "}
                  <Link
                    component={RouterLink}
                    to="/login"
                    state={{ from: location.state?.from }}
                    underline="hover"
                    sx={{
                      color: "#667eea",
                      fontWeight: 600,
                      fontSize: "1rem",
                      "&:hover": {
                        color: "#764ba2",
                      },
                    }}
                  >
                    Sign In
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: 2, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
